import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ContentCopy, CardGiftcard } from "@mui/icons-material";
import { getMembershipVoucherPointsCost } from "../config/membershipVoucherConfig";
import {
  generateMembershipVoucher,
  listMembershipVouchersByGenerator,
} from "../services/membership/MembershipVoucherService";

const MembershipVoucherPanel = ({
  user,
  userProfile,
  pointsBalance,
  onBalanceChange,
  primaryBlue = { 700: "#1E40AF", 500: "#3B82F6" },
}) => {
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [myVouchers, setMyVouchers] = useState([]);

  const { faceValue, totalPoints } = getMembershipVoucherPointsCost();
  const balance = Number(pointsBalance) || 0;
  const canGenerate = balance >= totalPoints;

  const loadMine = useCallback(async () => {
    if (!user?.uid) return;
    setListLoading(true);
    try {
      const rows = await listMembershipVouchersByGenerator(user.uid);
      setMyVouchers(rows);
    } catch (e) {
      console.error(e);
    } finally {
      setListLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadMine();
  }, [loadMine]);

  const handleGenerate = async () => {
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const result = await generateMembershipVoucher({
        generatorUid: user.uid,
        generatorEmail: user.email,
        generatorName: userProfile?.name,
        pointsBalance: balance,
      });
      if (!result.success) {
        setError(result.error);
        return;
      }
      setSuccess(
        `Voucher created: ${result.code}. Share this code with the person who should become an Ultra member.`
      );
      if (onBalanceChange) onBalanceChange();
      await loadMine();
    } catch (e) {
      setError(e.message || "Failed to generate voucher.");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    setSuccess(`Copied ${code} to clipboard.`);
  };

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <CardGiftcard sx={{ color: primaryBlue[500], mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: primaryBlue[700] }}>
            ₹{faceValue} Membership Vouchers
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>
          Your Points Wallet: <strong>{balance.toFixed(2)} pts</strong>
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        <Button
          variant="contained"
          disabled={loading || !canGenerate}
          onClick={handleGenerate}
          sx={{ mb: 2, bgcolor: primaryBlue[500] }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : `Generate ₹${faceValue} voucher`}
        </Button>

        {!canGenerate && balance > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Need {totalPoints.toFixed(2)} pts to generate a voucher.
          </Typography>
        )}

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Your vouchers
        </Typography>
        {listLoading ? (
          <CircularProgress size={28} />
        ) : myVouchers.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No vouchers generated yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Redeemed by</TableCell>
                  <TableCell align="right">Copy</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {myVouchers.slice(0, 20).map((v) => (
                  <TableRow key={v.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {v.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={v.status}
                        color={v.status === "active" ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell>
                      {v.redeemedByEmail || "—"}
                    </TableCell>
                    <TableCell align="right">
                      {v.status === "active" && (
                        <Tooltip title="Copy code">
                          <IconButton size="small" onClick={() => copyCode(v.code)}>
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default MembershipVoucherPanel;
